import { c as _c } from "react/compiler-runtime";
import { Partner } from "common/types/graphql";
import { useTranslations } from "modules/i18n/hooks/useTranslations";
import { usePageProps } from "common/hooks/data/usePageProps";
import type { EditPageProps } from "pages/edit/index.page";
import { ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import clsx from "classnames";
import { ImgixImage } from "common/components/ImgixImage";
import { PartnersProps } from "./Partners";
import { useState } from "react";
import { useRegionCode } from "modules/i18n/hooks/useRegionCode";
import { pageUrl } from "common/lib/router";

/**
 * TODO: New version of partners block, once all pages have been migrated, can replace Partners.view
 */

const MOBILE_MAX_COUNT = 6;
const TABLET_PLUS_MAX_COUNT = 5;
export const AVAILABLE_PARTNER_TYPES = ["founding_partner", "funding_partner", "member", "strategic_partner", "endorsing_partner", "content_partner", "media_partner", "hosting_partner", "technology_partner"];
interface PartnerTypeSectionsProps {
  partners?: Partner[];
  type: string;
  isActive: boolean;
  partnersPath: string;
  onClick: () => void;
}
export const PartnerTypeSections = t0 => {
  "use memo";

  const $ = _c(49);
  const {
    partners: t1,
    type,
    isActive,
    onClick,
    partnersPath
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const partners = t2;
  const t = useTranslations("blocks.partners");
  const {
    editing
  } = usePageProps();
  if (partners.length === 0) {
    return null;
  }
  let t3;
  if ($[2] !== type) {
    let t4;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = /_/g;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    t3 = type.replace(t4, "-");
    $[2] = type;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const partnerPathForType = partnersPath + `#${t3}s`;
  const t4 = `partner_heading_${type}`;
  const t5 = `partner_list_for_${type}`;
  let t6;
  if ($[5] !== t || $[6] !== type) {
    t6 = t(type);
    $[5] = t;
    $[6] = type;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== isActive) {
    t7 = <div className="ml-auto" aria-hidden="true">{isActive ? <ChevronUp className="size-5" /> : <ChevronDown className="size-5" />}</div>;
    $[8] = isActive;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== isActive || $[11] !== onClick || $[12] !== t5 || $[13] !== t6 || $[14] !== t7) {
    t8 = <button type="button" aria-expanded={isActive} aria-controls={t5} className="partners-block partners-block-accordion flex w-full items-center border-b pb-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-black" onClick={onClick}>{t6}{t7}</button>;
    $[10] = isActive;
    $[11] = onClick;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t4 || $[17] !== t8) {
    t9 = <h3 id={t4} className="font-sans-heading text-base leading-6 font-bold lg:text-xl">{t8}</h3>;
    $[16] = t4;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const t10 = `partner_list_for_${type}`;
  let t11;
  if ($[19] !== t || $[20] !== type) {
    t11 = t(type);
    $[19] = t;
    $[20] = type;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const t12 = !isActive;
  let t13;
  if ($[22] !== isActive || $[23] !== t12) {
    t13 = clsx("motion-safe:transition-all motion-safe:duration-500 motion-safe:ease-in-out", {
      "visible max-h-96 opacity-100": isActive,
      "invisible max-h-0 overflow-hidden opacity-0": t12
    });
    $[22] = isActive;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== editing || $[26] !== partners) {
    t14 = partners?.map((t15, index) => {
      const {
        path,
        logoUrl,
        name
      } = t15;
      return <a key={index} href={path} aria-label={name} className={clsx("group partners-block partners-block-click-through m-1 w-20 rounded-sm sm:w-14 md:w-16 lg:w-24", "focus:ring-2 focus:ring-black focus:ring-offset-4 focus:outline-none", {
        hidden: index + 1 > MOBILE_MAX_COUNT,
        "sm:hidden": index + 1 > TABLET_PLUS_MAX_COUNT,
        "w-14 sm:w-10 lg:w-12": editing
      })}><ImgixImage src={logoUrl} alt={name} dimensions={{
          height: 100,
          width: 150
        }} className="transition-all duration-300 group-hover:opacity-80 motion-safe:group-hover:-translate-y-1 motion-safe:group-hover:scale-[1.02]" fit="clip" /></a>;
    });
    $[25] = editing;
    $[26] = partners;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t14) {
    t15 = <div className="my-6 grid grid-cols-3 place-items-center gap-6 sm:grid-cols-5 lg:gap-y-10">{t14}</div>;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const t16 = `view_all_${type}`;
  let t17;
  if ($[30] !== t || $[31] !== t16) {
    t17 = t(t16);
    $[30] = t;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t17) {
    t18 = <span className="underline">{t17}</span>;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <ArrowRight className="ml-1 inline size-5 flex-none" aria-hidden="true" />;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== partnerPathForType || $[37] !== t18) {
    t20 = <a href={partnerPathForType} className="mb-3 flex text-sm font-medium hover:text-indigo-600">{t18}{t19}</a>;
    $[36] = partnerPathForType;
    $[37] = t18;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t10 || $[40] !== t11 || $[41] !== t13 || $[42] !== t15 || $[43] !== t20) {
    t21 = <div id={t10} role="region" aria-label={t11} className={t13}>{t15}{t20}</div>;
    $[39] = t10;
    $[40] = t11;
    $[41] = t13;
    $[42] = t15;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== t21 || $[46] !== t9 || $[47] !== type) {
    t22 = <div key={type} className="pb-5">{t9}{t21}</div>;
    $[45] = t21;
    $[46] = t9;
    $[47] = type;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  return t22;
};

/**
 * Basic group by function as Object.groupBy is not supported on enough browsers yet
 */
const partnerGroupByType = (partners?: Partner[]) => {
  if (!partners) return [];
  return partners.reduce((reducer, partner) => {
    reducer[partner.type] = reducer[partner.type] || [];
    reducer[partner.type].push(partner);
    return reducer;
  }, Object.create(null));
};
export interface PartnerExplainerProps {
  explainer?: string;
  partnerLinkDescription?: string;
  partnersPath: string;
}
const PartnerExplainer = t0 => {
  "use memo";

  const $ = _c(11);
  const {
    explainer,
    partnerLinkDescription,
    partnersPath
  } = t0;
  const t = useTranslations("blocks.partners");
  let t1;
  if ($[0] !== t) {
    t1 = t("explainer_section_label");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== explainer) {
    t2 = explainer && <p>{explainer}</p>;
    $[2] = explainer;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== partnerLinkDescription || $[5] !== partnersPath) {
    t3 = partnerLinkDescription && partnerLinkDescription.length > 0 && <a href={partnersPath} className="my-4 flex gap-1 text-sm font-medium hover:text-indigo-600"><span className="underline">{partnerLinkDescription}</span><ArrowRight className="inline size-5 flex-none" aria-hidden="true" /></a>;
    $[4] = partnerLinkDescription;
    $[5] = partnersPath;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <section className="col-span-full mr-16 break-words sm:col-span-3" aria-label={t1}>{t2}{t3}</section>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
export const AccordionPartnersView = t0 => {
  "use memo";

  const $ = _c(14);
  const {
    partners: t1,
    explainer,
    partnerLinkDescription
  } = t0;
  const partners = t1 === undefined ? [] : t1;
  const partnersByType = partnerGroupByType(partners);
  const [activePartnerIndex, setActivePartnerIndex] = useState(0);
  const partnerTypesToShow = AVAILABLE_PARTNER_TYPES.filter(partnerType => partnersByType[partnerType]);
  const regionCode = useRegionCode();
  let t2;
  if ($[0] !== regionCode) {
    t2 = pageUrl.view({
      regionCode,
      pageSlug: "partners"
    });
    $[0] = regionCode;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const partnersPath = t2;
  const t3 = "grid grid-flow-col grid-cols-8 gap-6";
  let t4;
  if ($[2] !== explainer || $[3] !== partnerLinkDescription || $[4] !== partnersPath) {
    t4 = explainer && <PartnerExplainer partnersPath={partnersPath} explainer={explainer} partnerLinkDescription={partnerLinkDescription} />;
    $[2] = explainer;
    $[3] = partnerLinkDescription;
    $[4] = partnersPath;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== explainer) {
    t5 = clsx("col-span-full", {
      "sm:col-span-5": explainer
    });
    $[6] = explainer;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = partnerTypesToShow.map((partnerType_0, index) => <PartnerTypeSections isActive={activePartnerIndex === index} key={partnerType_0} type={partnerType_0} partners={partnersByType[partnerType_0]} partnersPath={partnersPath} onClick={() => setActivePartnerIndex(activePartnerIndex === index ? -1 : index)} />);
  let t7;
  if ($[8] !== t5 || $[9] !== t6) {
    t7 = <div className={t5}>{t6}</div>;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t4 || $[12] !== t7) {
    t8 = <div className={t3}>{t4}{t7}</div>;
    $[11] = t4;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
};